import { useQuery } from '@apollo/client';

import { InventoryProduct } from '@/types/learning/learning-catalog';
import { Provider, ProviderAccessStatus, ProviderList, providers } from '@/store/providers';
import { useInventory } from '@/store/inventory';
import { LicenseStatus } from '@/administration/pages/Catalog/utils/types';
import { CourseraDocument } from '@/store/v2';

const courseraStatusMap: Partial<Record<LicenseStatus, ProviderAccessStatus>> = {
  [LicenseStatus.APPROVED]: ProviderAccessStatus.HAS_ACCESS,
  [LicenseStatus.NOT_APPROVED]: ProviderAccessStatus.NOT_APPROVED,
  [LicenseStatus.PENDING_APPROVAL]: ProviderAccessStatus.PENDING_ACCESS,
};

function hasInventoryInState(
  inventory: readonly InventoryProduct[],
  provider: Provider | null,
  state: InventoryProduct['state'],
) {
  return inventory.some((product) => product.providerAccess === provider && product.state === state);
}

function validateProviderStatus(
  provider: Provider | null,
  inventory: readonly InventoryProduct[],
  status: LicenseStatus,
): ProviderAccessStatus {
  if (!provider) return ProviderAccessStatus.NO_ACCESS;

  const meta = providers.find((entry) => entry.provider === provider);
  if (!meta?.requiresExplicitRegistration) return ProviderAccessStatus.HAS_ACCESS;

  if (provider === ProviderList.COURSERA) return courseraStatusMap[status] || ProviderAccessStatus.NO_ACCESS;

  const hasApproved = hasInventoryInState(inventory, provider, 'approved');
  if (hasApproved) return ProviderAccessStatus.HAS_ACCESS;

  const pendingAccess = hasInventoryInState(inventory, provider, 'pending approval');
  if (pendingAccess) return ProviderAccessStatus.PENDING_ACCESS;

  return ProviderAccessStatus.NO_ACCESS;
}

export function useProviderAccess(
  provider: Provider | null = null,
  setInitialProviderStatusIfReady?: (status: ProviderAccessStatus) => void,
): ProviderAccessStatus {
  const {
    inventory: { allProgress: inventory },
  } = useInventory();

  const { data: licenseData } = useQuery(CourseraDocument, {
    variables: { provider: ProviderList.COURSERA, currentRealm: window.realm },
    skip: provider !== ProviderList.COURSERA,
    onCompleted: (data) => {
      const status: LicenseStatus = data?.issuedLicense?.status;
      setInitialProviderStatusIfReady && setInitialProviderStatusIfReady(validateProviderStatus(provider, inventory, status));
    },
  });

  const status: LicenseStatus = licenseData?.issuedLicense?.status;

  return validateProviderStatus(provider, inventory, status);
}
